<template>
  <div class="profile-card" :class="cardClassRef">
    <button type="button" v-if="selectModeRef" class="overlay" @click="onClick"></button>
    <router-link v-if="linkModeRef" :to="linkUrlRef!" class="overlay"/>
    <div class="gender-bar" :class="genderClassRef"></div>
    <div class="profile-card-image" :class="{ 'placeholder': !personImageUrlRef }">
      <img :src="personImageSrcRef"/>
    </div>
    <div class="profile-card-body">
      <div class="title">
        {{ cardTitleRef }}
      </div>
      <div v-if="props.thisProfile" class="subtitle">
        (this profile)
      </div>
      <div v-else-if="props.showProfileType" class="subtitle">
        <LinkIcon v-if="profileTypeRef == ProfileType.ManagedLinked" size="1x"/>
        <LockIcon v-if="personRef?.restricted" size="1x"/>
        {{ profileTypeTextRef }}
        <UserLink v-if="ownerRef" :userId="ownerRef?.id" dimmed :static="static"/>
      </div>
    </div>
    <div class="profile-action">
      <label v-if="highlightMode && highlightSource" class="btn-highlight-label">
        <input type="checkbox" class="btn-highlight" :checked="isHighlightSourceRef" @change="onHighlightChange"/>
      </label>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-card {
  position: relative;
  display: flex;
  width: 100%;
  height: 40px;
  max-width: 400px;
  border-top: 1px solid #f8f8f8;
  padding: 0; // clear default button padding
  background-color: white;

  &:not(.flat) {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
  }

  &.raised,
  &.show-hover:hover {
    box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.15);
  }

  &.highlighted {
    background: var(--highlight-bg-color);
  }

  .gender-bar {
    flex: 0 0 8px;
    height: 100%;
    background-color: #e9e9e9;

    &.male {
      background-color: #c3d5ea;
    }

    &.female {
      background-color: #fadce1;
    }
  }

  .profile-card-image {
    flex: 0 0 36px;

    &.placeholder {
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f4f4f4;
      
      img {
        opacity: 20%;
      }
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .profile-card-body {
    align-self: center;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 0.5em;
    line-height: 1.3;
    overflow: hidden;

    .title {
      font-size: 0.875rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .subtitle {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.75rem;
      font-weight: 400;
      color: #888;
    }
  }

  &.empty, &.dangling {
    .profile-card-image {
      background-color: inherit;

      &.placeholder img {
        opacity: 60%;
      }
    }

    .title {
      color: #aaa;
    }
  }

  &.restricted {
    .profile-card-image {
      background-color: transparent;

      img {
        opacity: 80%;
      }
    }
  }

  &.this-profile {
    border: 1px dashed #ccc;

    &:not(.raised):not(.show-hover:hover) {
      box-shadow: none;
    }

    .gender-bar, .profile-card-image {
      opacity: 0;
    }
    
    .profile-card-body {
      .title {
        color: #888;
      }
      .subtitle {
        color: #aaa;
      }
    }
  }

  &.dimmed {
    .gender-bar {
      opacity: 0.4;
    }

    .profile-card-image {
      opacity: 0.4;
    }

    .title, .subtitle {
      color: #888;
    }
  }

  .profile-action {
    margin-left: auto;
    margin-right: 0.5rem;
    align-self: center;
  }
}

a.overlay {
  color: inherit;
  text-decoration: none;

  &:hover {
    color: inherit;
  }
}

button.overlay {
  border: none;
  text-align: left;
  cursor: pointer;
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import { genderImages, dottedPersonImage, Person } from '@/rd/ResearchDataModel'
import { usePersonStore } from '@/rd/PersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useUserStore } from '@/gp/UserStore'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import { ProfileUtils, ProfileType } from './profiles/ProfileUtils'
import { LinkIcon, LockIcon } from '@zhuowenli/vue-feather-icons'
import UserLink from '@/manage/users/UserLink.vue'

const props = defineProps<{
  personId?: string,
  selectMode?: boolean,
  static?: boolean,
  thisProfile?: boolean,
  showProfileType?: boolean,
  flat?: boolean,
  showHover?: boolean,
  disabled?: boolean,
  dimmed?: boolean,
  raised?: boolean,
  highlightMode?: boolean,
  highlighted?: boolean,
  highlightSource?: boolean,
  highlightSourceId?: string,
}>()

const emit = defineEmits<{
  (e: 'click', personId?: string): void,
  (e: 'highlight', personId?: string): void,
}>()

const personStore = usePersonStore()
const dataGroupStore = useDataGroupStore()
const userStore = useUserStore()

// Data loading
const personRef = computed(() => personStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded)?.data)
const displayPropsRef = computed(() => personRef.value?.displayProperties)
const dataGroupRef = computed(() => dataGroupStore.getAsyncGroup(CompositeId.getGroupId(props.personId), LoadMode.EnsureLoaded)?.data)
const ownerRef = computed(() => userStore.getAsyncUser(dataGroupRef.value?.ownerId, LoadMode.EnsureLoaded)?.data)

// Profile type
const profileTypeRef = computed(() => ProfileUtils.getProfileType(personRef.value, dataGroupRef.value, ownerRef.value))
const profileTypeTextRef = computed(() => ProfileUtils.getProfileSubtitle(profileTypeRef.value))

// Display state
const isEmptyRef = computed(() => !props.personId)
const isDanglingRef = computed(() => props.personId && personRef.value?.placeholder)
const isLinkableRef = computed(() => props.personId && personRef.value && !personRef.value.placeholder)

// Card content
const cardTitleRef = computed(() => 
  isEmptyRef.value ? '(none)' :
  isDanglingRef.value ? '(deleted or not visible)' : 
  displayPropsRef.value?.displayName ?? "(no name)"
)
const genderClassRef = computed(() => [displayPropsRef.value?.gender?.toLowerCase()])
const personImageUrlRef = computed(() => displayPropsRef.value?.primaryPhotoSignedUrl?.signedUrl)
const placeholderImageUrlRef = computed(() => 
  personRef.value?.restricted ? dottedPersonImage :
  !isLinkableRef.value ? dottedPersonImage :
  genderImages[displayPropsRef.value?.gender ?? "default"])
const personImageSrcRef = computed(() => personImageUrlRef.value ?? placeholderImageUrlRef.value)

const cardClassRef = computed(() => ({ 
  'this-profile': props.thisProfile,
  'restricted': personRef.value?.restricted,
  'empty': isEmptyRef.value,
  'dangling': isDanglingRef.value,
  'dimmed': props.dimmed,
  'flat': props.flat,
  'raised': props.raised,
  'show-hover': (isInteractiveRef.value && !props.flat) || props.showHover,
  'highlighted': props.highlightMode && props.highlighted
}))

const selectModeRef = computed(() => !props.static && props.selectMode && !isEmptyRef.value && !props.thisProfile && !props.disabled)
const linkModeRef = computed(() => !props.static && !props.selectMode && isLinkableRef.value && !props.thisProfile && !props.disabled)
const isInteractiveRef = computed(() => selectModeRef.value || linkModeRef.value)

const linkUrlRef = computed(() => linkModeRef.value ? Person.getPageUrl(props.personId) : undefined)

const isHighlightSourceRef = computed(() => props.highlightSource && props.highlightSourceId == props.personId)

function onClick(e: Event) {
  e.preventDefault()
  
  if (selectModeRef.value) {
    emit('click', props.personId)
  }
}

function onHighlightChange(e: Event) {
  const checkbox = e.target as HTMLInputElement
  emit('highlight', checkbox.checked ? props.personId : undefined)
}
</script>
